import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import './Donate.css';

const tiers = [
  {
    id: 'summit-delegate',
    amount: '$50',
    title: 'Sponsor a Summit Delegate',
    desc: 'Covers full Summit registration, materials, lunch, and workshop access for a talented student from an under-resourced community.',
    perk: 'Impact report + letter from your sponsored delegate',
    recommended: false,
  },
  {
    id: 'scholarship',
    amount: '$150',
    title: 'Full Summit Scholarship',
    desc: 'Covers travel stipend, accommodation, meals, and full access to the 2-day YOCILAB Youth Summit in Bamenda.',
    perk: 'Direct acknowledgment at Summit ceremony + impact portfolio',
    recommended: true,
  },
  {
    id: 'abeta-builder',
    amount: '$300',
    title: 'Empower an ABETA Builder',
    desc: 'Sponsors 6 months of AI and technology tracks, 1-on-1 mentorship, project resources, and builder portfolio verification.',
    perk: 'Virtual Builder Showcase pass + milestone progress tracking',
    recommended: false,
  },
  {
    id: 'seed-grant',
    amount: '$1,000',
    title: 'Microgrant for Youth Venture',
    desc: 'Provides a non-dilutive catalytic grant to a youth-led startup tackling climate, education, or employment in Cameroon.',
    perk: 'Quarterly pitch review sessions + direct venture advisory seat',
    recommended: false,
  },
];

const Donate = () => {
  const [selectedTier, setSelectedTier] = useState('scholarship');
  const [customAmount, setCustomAmount] = useState('');
  const [frequency, setFrequency] = useState('one-time');
  const [donorInfo, setDonorInfo] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="donate-page">
      <SEO
        title="Donate & Sponsor"
        description="Support African youth empowerment, scholarship funds for the YOCILAB Youth Summit, and builder grants through YOCILAB."
      />

      {/* Hero */}
      <section className="donate-hero">
        <div className="container">
          <div className="donate-hero__content">
            <span className="section-label section-label--accent">INVEST IN AFRICAN YOUTH</span>
            <h1 className="donate-hero__title">
              Fuel the Potential of Africa’s<br />
              <em>Next Generation Leaders</em>
            </h1>
            <p className="donate-hero__subtitle">
              Your contribution directly funds youth scholarships, AI &amp; technology training, hackathon mentorship, and seed grants that spark sustainable jobs.
            </p>
          </div>
        </div>
      </section>

      {/* Donation Form & Options */}
      <section className="donate-main">
        <div className="container">
          <div className="donate-layout">
            {/* Left: Giving Tiers */}
            <div className="donate-tiers">
              <h2 className="donate-section-title">Select Giving Tier</h2>
              <div className="donate-frequency-toggle">
                <button
                  className={`freq-btn ${frequency === 'one-time' ? 'active' : ''}`}
                  onClick={() => setFrequency('one-time')}
                >
                  One-time Gift
                </button>
                <button
                  className={`freq-btn ${frequency === 'monthly' ? 'active' : ''}`}
                  onClick={() => setFrequency('monthly')}
                >
                  Monthly Champion
                </button>
              </div>

              <div className="tiers-grid">
                {tiers.map((tier) => (
                  <div
                    key={tier.id}
                    className={`tier-card ${selectedTier === tier.id ? 'tier-card--selected' : ''} ${tier.recommended ? 'tier-card--popular' : ''}`}
                    onClick={() => {
                      setSelectedTier(tier.id);
                      setCustomAmount('');
                    }}
                  >
                    {tier.recommended && <span className="tier-badge">Most Popular</span>}
                    <div className="tier-header">
                      <span className="tier-amount">{tier.amount}</span>
                      <span className="tier-freq">{frequency === 'monthly' ? '/ month' : ''}</span>
                    </div>
                    <h3 className="tier-title">{tier.title}</h3>
                    <p className="tier-desc">{tier.desc}</p>
                    <div className="tier-perk">
                      <span className="perk-icon">✨</span>
                      <span>{tier.perk}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Checkout & Partner Box */}
            <div className="donate-sidebar">
              <div className="donate-card">
                <h3 className="donate-card__title">Complete Your Donation</h3>
                <p className="donate-card__subtitle">
                  100% of public donations directly fund youth programs, delegate scholarships, and builder grants.
                </p>

                {submitted ? (
                  <div className="donate-success">
                    <div className="donate-success__icon">🎉</div>
                    <h3>Thank you for your generous support!</h3>
                    <p>
                      Our team will reach out to <strong>{donorInfo.email}</strong> shortly with your donation confirmation and tax receipt details.
                    </p>
                    <button className="btn btn-primary" onClick={() => setSubmitted(false)}>
                      Make another contribution
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="donate-form">
                    <div className="form-group">
                      <label>Selected Tier:</label>
                      <div className="selected-summary">
                        <strong>
                          {tiers.find((t) => t.id === selectedTier)?.title || 'Custom Amount'}
                        </strong>
                        <span>
                          {customAmount ? `$${customAmount}` : tiers.find((t) => t.id === selectedTier)?.amount} {frequency === 'monthly' ? '/ month' : ''}
                        </span>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="customAmount">Or Enter Custom Amount ($ USD)</label>
                      <input
                        type="number"
                        id="customAmount"
                        min="5"
                        placeholder="e.g. 500"
                        value={customAmount}
                        onChange={(e) => {
                          setCustomAmount(e.target.value);
                          setSelectedTier('');
                        }}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="donorName">Full Name / Organization</label>
                      <input
                        type="text"
                        id="donorName"
                        required
                        placeholder="Jane Doe or Foundation Name"
                        value={donorInfo.name}
                        onChange={(e) => setDonorInfo({ ...donorInfo, name: e.target.value })}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="donorEmail">Email Address</label>
                      <input
                        type="email"
                        id="donorEmail"
                        required
                        placeholder="you@example.org"
                        value={donorInfo.email}
                        onChange={(e) => setDonorInfo({ ...donorInfo, email: e.target.value })}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="donorMsg">Dedication or Note (Optional)</label>
                      <textarea
                        id="donorMsg"
                        rows="2"
                        placeholder="In honor of African youth innovation..."
                        value={donorInfo.message}
                        onChange={(e) => setDonorInfo({ ...donorInfo, message: e.target.value })}
                      />
                    </div>

                    <button type="submit" className="btn btn-accent btn-lg donate-submit-btn">
                      Proceed to Secure Giving
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>

                    <div className="donate-trust">
                      <span>🔒 256-bit Secure Encryption</span>
                      <span>🌍 Direct African Impact</span>
                    </div>
                  </form>
                )}
              </div>

              {/* Corporate Partnerships note */}
              <div className="corporate-box">
                <h4>Institutional &amp; Corporate Giving</h4>
                <p>
                  Looking to fund an entire cohort, co-brand a summit stage, or sponsor an innovation challenge?
                </p>
                <Link to="/contact" className="corporate-link">
                  Speak with our Partnerships Team →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;